import { Box, BoxProps, SystemStyleObject } from '@chakra-ui/react';
import React from 'react';
// eslint-disable-next-line no-restricted-imports
import ReactDatepicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

export type DatePickerProps = {
  selectedDate: Date | undefined;
  onChange: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  id?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;
  sx?: BoxProps['sx'];
};

const selectStyle: SystemStyleObject = {
  color: 'content.title',
  bgColor: 'surface.sidebarMuted',
  border: '1px solid',
  borderColor: 'surface.border',
  borderRadius: '4px',
  px: '4px',
  py: '2px',
  mt: '10px',
  _focus: {
    outline: 'none',
    borderColor: 'icon.green',
    boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
  },
};

// Token overrides for the library's light-only default CSS
const calendarStyle: SystemStyleObject = {
  '.react-datepicker': {
    bg: 'surface.panel',
    border: '1px solid',
    borderColor: 'surface.border',
    fontFamily: 'marketingPage',
  },
  '.react-datepicker__header': {
    bg: 'surface.sidebarMuted',
    borderBottomColor: 'surface.border',
  },
  '.react-datepicker__current-month, .react-datepicker-time__header': {
    color: 'content.title',
  },
  '.react-datepicker__day-name': {
    color: 'content.muted',
  },
  '.react-datepicker__day': {
    color: 'content.body',
    _hover: {
      bg: 'border.lightGreen',
    },
  },
  '.react-datepicker__day--selected': {
    bg: 'icon.green',
    color: 'white',
    _hover: {
      bg: 'icon.green',
    },
  },
  '.react-datepicker__day--keyboard-selected': {
    bg: 'surface.sidebarMuted',
    color: 'content.title',
  },
  '.react-datepicker__day--today': {
    fontWeight: 'bold',
    color: 'content.title',
  },
  '.react-datepicker__day--outside-month, .react-datepicker__day--disabled': {
    color: 'content.muted',
    opacity: 0.5,
  },
  '.react-datepicker__navigation-icon::before': {
    borderColor: 'content.muted',
  },
};

export const DatePicker = React.forwardRef<ReactDatepicker, DatePickerProps>(
  ({ selectedDate, onChange, id, onBlur, sx, minDate, maxDate }, ref) => {
    return (
      <Box
        sx={{
          ...calendarStyle,
          ...sx,
          '.react-datepicker__month-select': selectStyle,
          '.react-datepicker__year-select': selectStyle,
        }}>
        <ReactDatepicker
          disabledKeyboardNavigation
          ref={ref}
          id={id}
          clearButtonTitle="clear"
          isClearable
          inline
          minDate={minDate}
          maxDate={maxDate}
          openToDate={selectedDate ?? undefined}
          selected={selectedDate}
          showMonthDropdown
          showYearDropdown
          onBlur={onBlur}
          dropdownMode="select"
          onChange={(updatedStart: Date | null) => {
            if (updatedStart) {
              onChange(updatedStart);
            }
          }}
        />
      </Box>
    );
  },
);

DatePicker.displayName = 'DatePicker';
